import { supabase } from '@/config/supabase';
import { formatNumber } from '@/utils/format';
import { ACHIEVEMENTS } from '@/models/achievements';

export class ProfilePanel {
  private container: HTMLElement;
  private contentEl: HTMLElement;
  private onBack: () => void;

  constructor(parent: HTMLElement, onBack: () => void) {
    this.onBack = onBack;

    this.container = document.createElement('div');
    this.container.id = 'profile-panel';
    this.container.className = 'game-panel';
    this.container.style.cssText = 'display:none; width:320px; max-height:80vh;';

    this.container.innerHTML = `
      <div class="game-panel-header">
        <div class="game-panel-title">ARCHON PROFILE</div>
        <button class="game-panel-close">&times;</button>
      </div>
    `;

    this.contentEl = document.createElement('div');
    this.container.appendChild(this.contentEl);

    // Back button
    const backBtn = document.createElement('button');
    backBtn.className = 'btn';
    backBtn.style.cssText = 'width:100%; padding:10px; margin-top:12px; color:var(--gold); background:var(--bg-button);';
    backBtn.textContent = '← Back to Game';
    backBtn.addEventListener('click', () => this.onBack());
    this.container.appendChild(backBtn);

    // Share button
    const shareBtn = document.createElement('button');
    shareBtn.className = 'btn';
    shareBtn.style.cssText = 'width:100%; padding:8px; margin-top:6px; color:var(--text-dim); background:var(--bg-button); font-size:11px;';
    shareBtn.textContent = '🔗 Copy Profile Link';
    shareBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(window.location.href);
      shareBtn.textContent = '✓ Copied!';
      setTimeout(() => shareBtn.textContent = '🔗 Copy Profile Link', 2000);
    });
    this.container.appendChild(shareBtn);

    this.container.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());
    parent.appendChild(this.container);
  }

  async loadProfile(userId: string): Promise<void> {
    this.contentEl.innerHTML = '<div style="text-align:center; color:var(--text-dim); padding:20px;">Loading...</div>';
    this.show();

    const { data, error } = await supabase
      .from('players')
      .select('username, era, prestige_count, production_rate, unlocked_achievements, created_at')
      .eq('id', userId)
      .single();

    if (error || !data) {
      this.contentEl.innerHTML = '<div style="text-align:center; color:var(--red); padding:20px;">Archon not found</div>';
      return;
    }

    const ERA_NAMES: Record<string, string> = {
      ancient: '⚱️ Ancient', medieval: '🏰 Medieval', modern: '🔭 Renaissance',
      industrial: '⚙️ Industrial', contemporary: '🏙️ Modern', future: '🚀 Future',
    };

    const unlocked: string[] = (data as { unlocked_achievements?: string[] }).unlocked_achievements ?? [];
    const badges = ACHIEVEMENTS
      .filter(a => unlocked.includes(a.id))
      .map(a => `<span title="${a.name}" style="font-size:18px;">${a.icon}</span>`)
      .join(' ');

    const playerData = data as {
      username: string;
      era: string;
      prestige_count: number;
      production_rate: number;
      created_at: string;
    };

    this.contentEl.innerHTML = `
      <div style="text-align:center; margin-bottom:16px;">
        <div style="font-size:20px; color:var(--gold); font-weight:700; font-family:var(--font-display);">${playerData.username}</div>
        <div style="font-size:12px; color:var(--text-dim); margin-top:4px;">${ERA_NAMES[playerData.era] ?? playerData.era}</div>
      </div>
      <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:16px;">
        <div class="profile-stat">
          <div class="profile-stat-value">${playerData.prestige_count}</div>
          <div class="profile-stat-label">Prestiges</div>
        </div>
        <div class="profile-stat">
          <div class="profile-stat-value">${formatNumber(playerData.production_rate)}/s</div>
          <div class="profile-stat-label">Production</div>
        </div>
      </div>
      ${badges ? `<div style="margin-bottom:12px;"><div style="font-size:11px; color:var(--text-dim); margin-bottom:6px;">Badges (${unlocked.length})</div><div style="line-height:1.8;">${badges}</div></div>` : ''}
      <div style="font-size:10px; color:var(--text-muted); text-align:center;">Joined ${new Date(playerData.created_at).toLocaleDateString()}</div>
    `;
  }

  show(): void { this.container.style.display = 'block'; }
  hide(): void { this.container.style.display = 'none'; }
  destroy(): void { this.container.remove(); }
}
